import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  borrowerPairs as initialPairs,
  complianceFindings,
  disclosureDocuments,
  disclosureHistory as initialHistory,
  getIntegrationError,
  readinessChecks,
  validationIssues,
} from '../data/loan'
import type {
  BorrowerPair,
  DisclosureHistoryEntry,
  GenerationStage,
  IntegrationError,
  PackageState,
  Recipient,
  Scenario,
  SendOptions,
  ValidationState,
  WorkflowStep,
} from '../types/disclosures'
import { formatHistoryStamp, formatRelativeStamp } from '../utils/format'

const emptyPackage: PackageState = {
  status: 'not_generated',
  documents: [],
  selectedDocumentIds: [],
  generatedAt: null,
  sentAt: null,
  delivery: null,
  recipientIds: [],
}

const runningValidation: ValidationState = {
  phase: 'running',
  checks: readinessChecks.map((check) => ({ ...check, state: 'passed' })),
  issues: [],
  complianceFindings: [],
  error: null,
}

const generationStageLabels = [
  'Validating loan',
  'Building disclosure package',
  'Preparing documents',
]

function buildRecord<T>(pairs: BorrowerPair[], value: () => T): Record<string, T> {
  return pairs.reduce<Record<string, T>>((acc, pair) => {
    acc[pair.id] = value()
    return acc
  }, {})
}

function resolveValidation(scenario: Scenario): ValidationState {
  if (scenario === 'connection') {
    return {
      phase: 'error',
      checks: [],
      issues: [],
      complianceFindings: [],
      error: getIntegrationError('connection'),
    }
  }

  if (scenario === 'issues') {
    return {
      phase: 'issues',
      checks: readinessChecks.map((check) =>
        check.id === 'borrower' || check.id === 'loan'
          ? { ...check, state: 'issue' }
          : check,
      ),
      issues: validationIssues,
      complianceFindings: complianceFindings,
      error: null,
    }
  }

  if (scenario === 'no_package') {
    return {
      phase: 'issues',
      checks: readinessChecks.map((check) =>
        check.id === 'package' || check.id === 'ready'
          ? { ...check, state: 'issue' }
          : check,
      ),
      issues: [validationIssues[2]],
      complianceFindings: [],
      error: getIntegrationError('no_package'),
    }
  }

  return {
    phase: 'passed',
    checks: readinessChecks,
    issues: [],
    complianceFindings: scenario === 'ready' ? [] : complianceFindings,
    error: null,
  }
}

export function useDisclosureWorkflow() {
  const [scenario, setScenario] = useState<Scenario>('ready')
  const [pairs, setPairs] = useState<BorrowerPair[]>(initialPairs)
  const [activePairId, setActivePairId] = useState(initialPairs[0].id)
  const [packages, setPackages] = useState<Record<string, PackageState>>(() =>
    buildRecord(initialPairs, () => ({ ...emptyPackage })),
  )
  const [validation, setValidation] = useState<Record<string, ValidationState>>(() =>
    buildRecord(initialPairs, () => runningValidation),
  )
  const [stages, setStages] = useState<Record<string, GenerationStage[]>>({})
  const [errors, setErrors] = useState<Record<string, IntegrationError | null>>({})
  const [history, setHistory] = useState<DisclosureHistoryEntry[]>(initialHistory)
  const [retriedGeneration, setRetriedGeneration] = useState<Record<string, boolean>>({})

  const timers = useRef<number[]>([])
  const schedule = useCallback((fn: () => void, delay: number) => {
    const id = window.setTimeout(fn, delay)
    timers.current.push(id)
  }, [])

  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    },
    [],
  )

  const runValidation = useCallback(
    (pairId: string, nextScenario: Scenario) => {
      setValidation((prev) => ({ ...prev, [pairId]: runningValidation }))
      schedule(() => {
        setValidation((prev) => ({ ...prev, [pairId]: resolveValidation(nextScenario) }))
      }, 850)
    },
    [schedule],
  )

  // Initial + scenario-driven validation for every borrower pair.
  useEffect(() => {
    pairs.forEach((pair) => {
      const isBlockedScenario =
        scenario === 'issues' || scenario === 'no_package' || scenario === 'connection'
      runValidation(pair.id, pair.id === activePairId || isBlockedScenario ? scenario : 'ready')
    })
    setPackages(buildRecord(pairs, () => ({ ...emptyPackage })))
    setErrors({})
    setStages({})
    setRetriedGeneration({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario])

  const retryValidation = useCallback(
    (pairId: string) => {
      // Assume the user resolved the flagged items outside of this screen.
      runValidation(pairId, 'ready')
    },
    [runValidation],
  )

  const generate = useCallback(
    (pairId: string) => {
      const willFail =
        (scenario === 'locked' || scenario === 'already_sent') && !retriedGeneration[pairId]

      setErrors((prev) => ({ ...prev, [pairId]: null }))
      setPackages((prev) => ({
        ...prev,
        [pairId]: { ...emptyPackage, status: 'preparing' },
      }))
      setStages((prev) => ({
        ...prev,
        [pairId]: generationStageLabels.map((label, index) => ({
          id: label,
          label,
          state: index === 0 ? 'active' : 'pending',
        })),
      }))

      const advance = (index: number) =>
        setStages((prev) => ({
          ...prev,
          [pairId]: generationStageLabels.map((label, i) => ({
            id: label,
            label,
            state: i < index ? 'done' : i === index ? 'active' : 'pending',
          })),
        }))

      schedule(() => advance(1), 900)
      schedule(() => advance(2), 1800)
      schedule(() => {
        if (willFail) {
          setRetriedGeneration((prev) => ({ ...prev, [pairId]: true }))
          setErrors((prev) => ({
            ...prev,
            [pairId]: getIntegrationError(scenario === 'locked' ? 'loan_locked' : 'already_sent'),
          }))
          setPackages((prev) => ({
            ...prev,
            [pairId]: { ...emptyPackage, status: 'action_required' },
          }))
          return
        }

        const included = disclosureDocuments.filter((doc) => !doc.excluded)
        setPackages((prev) => ({
          ...prev,
          [pairId]: {
            ...emptyPackage,
            status: 'ready',
            documents: disclosureDocuments,
            selectedDocumentIds: included.map((doc) => doc.id),
            generatedAt: formatRelativeStamp(),
          },
        }))
      }, 2800)
    },
    [retriedGeneration, scenario, schedule],
  )

  const generateAll = useCallback(() => {
    pairs.forEach((pair) => {
      if (validation[pair.id]?.phase === 'passed' && packages[pair.id]?.status !== 'ready') {
        generate(pair.id)
      }
    })
  }, [generate, packages, pairs, validation])

  const regenerate = useCallback(
    (pairId: string) => {
      setRetriedGeneration((prev) => ({ ...prev, [pairId]: true }))
      generate(pairId)
    },
    [generate],
  )

  const toggleDocument = useCallback((pairId: string, documentId: string) => {
    setPackages((prev) => {
      const target = prev[pairId]
      if (!target) return prev
      const doc = target.documents.find((item) => item.id === documentId)
      if (!doc || doc.required || doc.excluded) return prev
      const selected = target.selectedDocumentIds.includes(documentId)
        ? target.selectedDocumentIds.filter((id) => id !== documentId)
        : [...target.selectedDocumentIds, documentId]
      return { ...prev, [pairId]: { ...target, selectedDocumentIds: selected } }
    })
  }, [])

  const updateRecipient = useCallback((pairId: string, recipient: Recipient) => {
    setPairs((prev) =>
      prev.map((pair) =>
        pair.id === pairId
          ? {
              ...pair,
              recipients: pair.recipients.map((item) =>
                item.id === recipient.id ? recipient : item,
              ),
            }
          : pair,
      ),
    )
  }, [])

  const completeSend = useCallback(
    (pairId: string, options: SendOptions) => {
      const pair = pairs.find((item) => item.id === pairId)
      const target = packages[pairId]
      const documentCount = target?.selectedDocumentIds.length ?? 0
      const sentAt = formatRelativeStamp()

      setPackages((prev) => ({
        ...prev,
        [pairId]: {
          ...prev[pairId],
          status: 'sent',
          sentAt,
          delivery: options.delivery,
          recipientIds: options.recipientIds,
        },
      }))
      setHistory((prev) => [
        {
          id: `h-${Date.now()}`,
          title: 'Initial Disclosures',
          status: 'Sent',
          sentAt: formatHistoryStamp(),
          pairLabel: pair?.label ?? '',
          documentCount,
          delivery: options.delivery === 'electronic' ? 'Electronic' : 'Paper / Fulfillment',
        },
        ...prev,
      ])
    },
    [packages, pairs],
  )

  const activePair = useMemo(
    () => pairs.find((pair) => pair.id === activePairId) ?? pairs[0],
    [activePairId, pairs],
  )
  const activePackage = packages[activePairId] ?? emptyPackage
  const activeValidation = validation[activePairId] ?? runningValidation
  const activeStages = stages[activePairId] ?? []
  const activeError = errors[activePairId] ?? activeValidation.error ?? null

  const currentStep: WorkflowStep =
    activePackage.status === 'preparing'
      ? 'generate'
      : activePackage.status === 'ready' || activePackage.status === 'sent'
        ? 'send'
        : 'review'

  return {
    scenario,
    setScenario,
    pairs,
    activePair,
    activePairId,
    setActivePairId,
    packages,
    validation,
    activePackage,
    activeValidation,
    activeStages,
    activeError,
    currentStep,
    history,
    generate,
    generateAll,
    regenerate,
    retryValidation,
    toggleDocument,
    updateRecipient,
    completeSend,
  }
}
